import { useMemo } from "react"

import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table"
import { Download } from "lucide-react"
import { DateTime } from "luxon"

import { Button } from "@/components/ui/button.tsx"
import { Progress } from "@/components/ui/progress.tsx"
import { Skeleton } from "@/components/ui/skeleton.tsx"
import {
  Table,
  TableBody,
  TableHead,
  TableCell,
  TableHeader,
  TableRow,
} from "@/components/ui/table.tsx"
import type { ReportRun } from "@/features/datapoints/types/reportTypes.ts"

import { downloadRun } from "../../api/report/downloadRun.ts"

const columnHelper = createColumnHelper<ReportRun>()

export default function ReportRunsTable({
  data,
  isLoading,
}: {
  data: ReportRun[]
  isLoading: boolean
}) {
  const columnDefs = useMemo(
    () => [
      columnHelper.accessor((run) => run.reportName, {
        id: "names",
        header: () => <div className="p-4 w-[500px]">Report</div>,
        cell: (context) => <div className="p-4">{context.getValue()}</div>,
      }),
      columnHelper.accessor((run) => run.runTime, {
        id: "runTime",
        header: () => <div>Run Time</div>,
        cell: (context) => (
          <div>{DateTime.fromISO(context.getValue()).toLocaleString(DateTime.DATETIME_MED)}</div>
        ),
      }),
      columnHelper.accessor((run) => run.state, {
        id: "state",
        header: () => <div className="w-[200px]">Status</div>,
        cell: (context) => (
          <div>
            {context.getValue() == "Running" ? (
              <Progress
                value={
                  ((context.row.original.currentChunk || 0) * 100) /
                  (context.row.original.chunkCount ? context.row.original.chunkCount : 1)
                }
              />
            ) : (
              context.getValue()
            )}
          </div>
        ),
      }),
      columnHelper.display({
        id: "download",
        header: () => <div className="w-[100px]">Download</div>,
        cell: ({ row }) => (
          <div>
            <Button
              variant="ghost"
              onClick={() => downloadRun(row.original.id)}
              disabled={row.original.state != "Complete"}
            >
              <Download />
            </Button>
          </div>
        ),
      }),
    ],
    [],
  )

  const table = useReactTable<ReportRun>({
    data: data,
    columns: columnDefs,
    getCoreRowModel: getCoreRowModel(),
  })

  return (
    <Table>
      {table.getHeaderGroups().map((hg) => (
        <TableHeader className="bg-card-header" key={hg.id}>
          <TableRow>
            {hg.headers.map((header, i) => (
              <TableHead key={i}>
                {flexRender(header.column.columnDef.header, header.getContext())}
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
      ))}
      <TableBody>
        {isLoading
          ? [...Array(10).keys()].map((i) => (
              <TableRow key={i}>
                <TableCell colSpan={4} className="p-4 h-[53px] box-border">
                  <Skeleton className="w-full h-full" />
                </TableCell>
              </TableRow>
            ))
          : table.getRowModel().rows.map((row) => (
              <TableRow key={row.id}>
                {row.getVisibleCells().map((cell, i) => (
                  <TableCell key={i}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                ))}
              </TableRow>
            ))}
      </TableBody>
    </Table>
  )
}
